import { ConfirmConfig } from '@ardoq/modal';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type ArdoqError, isTruthy } from '@ardoq/common-helpers';

export const getConfirmMultiUpdateFailedDialog = (
  i18nLocale: I18nLocale,
  result: ArdoqError,
  traceId: string
): ConfirmConfig => {
  return {
    title: t(
      i18nLocale,
      'inventory:bulkUpdateFailed.title',
      'Bulk update failed'
    ),
    text: [
      result.userMessage,
      t(
        i18nLocale,
        'inventory:bulkUpdateFailed.revertedNotice',
        'Your changes could not be saved and will be reverted'
      ),
    ].filter(isTruthy),
    ...(traceId && {
      text2: t(
        i18nLocale,
        'inventory:bulkUpdateFailed.traceId',
        { traceId },
        `Trace ID: ${traceId}`
      ),
    }),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  };
};

export const getBulkDeleteFailedDialog = (
  i18nLocale: I18nLocale,
  result: ArdoqError,
  traceId: string
): ConfirmConfig => {
  return {
    title: t(
      i18nLocale,
      'inventory:bulkDeleteFailed.title',
      'Bulk delete failed'
    ),
    text: [
      result.userMessage,
      t(
        i18nLocale,
        'inventory:bulkDeleteFailed.nothingDeletedNotice',
        'No components were deleted.'
      ),
    ].filter(isTruthy),
    ...(traceId && {
      text2: t(
        i18nLocale,
        'inventory:bulkDeleteFailed.traceId',
        { traceId },
        `Trace ID: ${traceId}`
      ),
    }),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  };
};

export const getConfirmCannotMoveComponentsDialog = (
  i18nLocale: I18nLocale,
  validationResult: { isValid: false; errorMessages: string[] }
): ConfirmConfig => {
  return {
    title: t(
      i18nLocale,
      'inventory:cannotMoveComponents.title',
      'Cannot move components'
    ),
    text: t(
      i18nLocale,
      'inventory:cannotMoveComponents.text',
      { errors: validationResult.errorMessages.join(' ') },
      `Your changes could not be applied. ${validationResult.errorMessages.join(' ')}`
    ),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  };
};

export const getMultiUpdateConfirmationDialog = (
  numberOfComponentsToUpdate: number,
  i18nLocale: I18nLocale
): ConfirmConfig => {
  return {
    title: t(
      i18nLocale,
      'inventory:confirmDialogs.multiUpdate.title',
      'Confirm bulk update'
    ),
    text: t(
      i18nLocale,
      'inventory:confirmDialogs.multiUpdate.text',
      { count: numberOfComponentsToUpdate },
      `You are about to edit the value of ${numberOfComponentsToUpdate} ${numberOfComponentsToUpdate === 1 ? 'component' : 'components'}. Are you sure you want to do this?`
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'inventory:confirmDialogs.multiUpdate.confirmButtonTitle',
      'Update components'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'inventory:confirmDialogs.cancel',
      'Cancel'
    ),
  };
};

const getSkippedNoTripleLine = (
  skippedNoTripleCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceUpdate.skippedNoTriple',
    { count: skippedNoTripleCount },
    skippedNoTripleCount === 1
      ? '1 reference won’t be created because your metamodel doesn’t allow it between those component types.'
      : `${skippedNoTripleCount} references won’t be created because your metamodel doesn’t allow them between their component types.`
  );

const getUnloadedComponentLine = (
  unloadedComponentCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceUpdate.unloadedComponent',
    { count: unloadedComponentCount },
    unloadedComponentCount === 1
      ? '1 reference won’t be created because we couldn’t load the component it points to.'
      : `${unloadedComponentCount} references won’t be created because we couldn’t load the components they point to.`
  );

const getUnmatchedNameLine = (
  unmatchedNameCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceUpdate.unmatchedName',
    { count: unmatchedNameCount },
    unmatchedNameCount === 1
      ? 'No component carries the name in 1 of the cells you pasted.'
      : `No component carries the names in ${unmatchedNameCount} of the cells you pasted.`
  );

const getAmbiguousNameLine = (
  ambiguousNameCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceUpdate.ambiguousName',
    { count: ambiguousNameCount },
    ambiguousNameCount === 1
      ? 'Several components carry the name in 1 of the cells you pasted. Paste the id instead.'
      : `Several components carry the names in ${ambiguousNameCount} of the cells you pasted. Paste the ids instead.`
  );

const getIneligibleParentLine = (
  ineligibleParentCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.unresolvedNames.ineligibleParent',
    { count: ineligibleParentCount },
    ineligibleParentCount === 1
      ? 'In 1 of the cells you pasted, the named component can’t be the parent: it’s in another workspace, or it’s the component itself or one of its descendants.'
      : `In ${ineligibleParentCount} of the cells you pasted, the named components can’t be the parent: they’re in another workspace, or they’re the component itself or one of its descendants.`
  );

const getBlockedCreateLine = (
  blockedCreateCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceUpdate.blockedCreate',
    { count: blockedCreateCount },
    blockedCreateCount === 1
      ? '1 reference won’t be created because you don’t have permission to edit its source component.'
      : `${blockedCreateCount} references won’t be created because you don’t have permission to edit their source components.`
  );

const getBlockedByPermissionsLine = (
  blockedDeleteCount: number,
  i18nLocale: I18nLocale
) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.bulkReferenceDelete.blockedByPermissions',
    { count: blockedDeleteCount },
    blockedDeleteCount === 1
      ? '1 reference won’t be deleted because you don’t have permission to edit it.'
      : `${blockedDeleteCount} references won’t be deleted because you don’t have permission to edit them.`
  );

const getReferenceConfirmButtonTitle = (
  createCount: number,
  deleteCount: number,
  i18nLocale: I18nLocale
) => {
  if (createCount > 0 && deleteCount > 0) {
    return t(
      i18nLocale,
      'inventory:confirmDialogs.bulkReferenceUpdate.confirmButtonTitle',
      'Update references'
    );
  }
  if (createCount > 0) {
    return t(
      i18nLocale,
      'inventory:confirmDialogs.bulkReferenceUpdate.createButtonTitle',
      'Create references'
    );
  }
  if (deleteCount > 0) {
    return t(
      i18nLocale,
      'inventory:confirmDialogs.bulkReferenceDelete.confirmButtonTitle',
      'Delete references'
    );
  }
  return t(
    i18nLocale,
    'inventory:confirmDialogs.multiUpdate.confirmButtonTitle',
    'Update components'
  );
};

export const getBulkReferenceDeleteConfirmationDialog = ({
  createCount,
  deleteCount,
  componentCount,
  blockedDeleteCount,
  fieldUpdateComponentCount,
  i18nLocale,
}: {
  createCount: number;
  deleteCount: number;
  /** Components whose references change, not the size of the whole operation. */
  componentCount: number;
  blockedDeleteCount: number;
  /** Components with field or tag changes in the same operation. */
  fieldUpdateComponentCount: number;
  i18nLocale: I18nLocale;
}): ConfirmConfig => {
  return {
    title: t(
      i18nLocale,
      'inventory:confirmDialogs.bulkReferenceDelete.title',
      'Confirm bulk update'
    ),
    text: [
      createCount > 0 &&
        t(
          i18nLocale,
          'inventory:confirmDialogs.bulkReferenceUpdate.createText',
          { count: createCount },
          createCount === 1
            ? 'You are about to create 1 reference.'
            : `You are about to create ${createCount} references.`
        ),
      deleteCount > 0 &&
        t(
          i18nLocale,
          'inventory:confirmDialogs.bulkReferenceDelete.text',
          { count: deleteCount },
          deleteCount === 1
            ? 'You are about to delete 1 reference.'
            : `You are about to delete ${deleteCount} references.`
        ),
      componentCount > 1 &&
        t(
          i18nLocale,
          'inventory:confirmDialogs.bulkReferenceDelete.componentCount',
          { count: componentCount },
          `This affects ${componentCount} components.`
        ),
      blockedDeleteCount > 0 &&
        getBlockedByPermissionsLine(blockedDeleteCount, i18nLocale),
      fieldUpdateComponentCount > 0 &&
        t(
          i18nLocale,
          'inventory:confirmDialogs.bulkReferenceDelete.componentUpdates',
          { count: fieldUpdateComponentCount },
          fieldUpdateComponentCount === 1
            ? 'You are also about to edit the value of 1 component.'
            : `You are also about to edit the value of ${fieldUpdateComponentCount} components.`
        ),
    ].filter(isTruthy),
    confirmButtonTitle: getReferenceConfirmButtonTitle(
      createCount,
      deleteCount,
      i18nLocale
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'inventory:confirmDialogs.cancel',
      'Cancel'
    ),
  };
};

const getNothingChangedTitle = (i18nLocale: I18nLocale) =>
  t(
    i18nLocale,
    'inventory:confirmDialogs.nothingChanged.title',
    'Nothing was changed'
  );

/**
 * A paste naming components we cannot pin down saves nothing at all, so the
 * names are explained on their own.
 */
export const getUnresolvedNamesDialog = ({
  unmatchedNameCount,
  ambiguousNameCount,
  ineligibleParentCount,
  i18nLocale,
}: {
  unmatchedNameCount: number;
  ambiguousNameCount: number;
  ineligibleParentCount: number;
  i18nLocale: I18nLocale;
}): ConfirmConfig => ({
  title: t(
    i18nLocale,
    'inventory:confirmDialogs.unresolvedNames.title',
    'Nothing was pasted'
  ),
  text: [
    unmatchedNameCount > 0 &&
      getUnmatchedNameLine(unmatchedNameCount, i18nLocale),
    ambiguousNameCount > 0 &&
      getAmbiguousNameLine(ambiguousNameCount, i18nLocale),
    ineligibleParentCount > 0 &&
      getIneligibleParentLine(ineligibleParentCount, i18nLocale),
    t(
      i18nLocale,
      'inventory:confirmDialogs.unresolvedNames.allOrNothing',
      'A paste is applied in full, so the other cells were left alone too.'
    ),
  ].filter(isTruthy),
  confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
  cancelButtonTitle: '',
});

/**
 * Nothing could be sent, so there is nothing to confirm: the user is told why
 * and the cells are restored either way.
 */
export const getNothingChangedDialog = ({
  blockedDeleteCount,
  skippedNoTripleCount,
  unloadedComponentCount,
  blockedCreateCount,
  i18nLocale,
}: {
  blockedDeleteCount: number;
  skippedNoTripleCount: number;
  unloadedComponentCount: number;
  blockedCreateCount: number;
  i18nLocale: I18nLocale;
}): ConfirmConfig => ({
  title: getNothingChangedTitle(i18nLocale),
  text: [
    blockedDeleteCount > 0 &&
      getBlockedByPermissionsLine(blockedDeleteCount, i18nLocale),
    skippedNoTripleCount > 0 &&
      getSkippedNoTripleLine(skippedNoTripleCount, i18nLocale),
    unloadedComponentCount > 0 &&
      getUnloadedComponentLine(unloadedComponentCount, i18nLocale),
    blockedCreateCount > 0 &&
      getBlockedCreateLine(blockedCreateCount, i18nLocale),
  ].filter(isTruthy),
  confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
  cancelButtonTitle: '',
});
